import * as React from 'react'
import * as cn from 'classnames'
import * as styles from './Card.module.scss'
import Packshot from './Packshot/Packshot'
import TextOverlay from './TextOverlay'
import ProgressBar from './ProgressBar'
import CloseButton from './CloseButton'
import * as csm from '../../../../utils/csm'
import LiveStatus from './LiveStatus/LiveStatus'
import { FeatureSwitches, StorefrontStateItem } from '../../../../containers/Storefront';
import { Hover } from '../../../../../../DVWebNodeHover/Hover'
import { TrailerFragment, StorefrontItemPlaybackAction, ItemProgressBar, safeBool, HideThisOutput, withArgs, Args, StorefrontItem, LinearBadge } from 'DVWebNodeUtils'
import { HideThisOverlay } from './HideThis/HideThisOverlay'
import { CollectionType } from '../../Collection'
import { GlobalQueueHandler, EnrichmentMap } from '../../../../../../../Explore/utils/GlobalQueueHandler'
import { safeFeature } from '../../../../../../../Explore/DVWebNodeStorefront/shared/utils/featureSwitches'
import { FollowingButtonContainer } from '@shared/person/toggleFollowingButton/FollowingButtonContainer'

type RemoveItem = (removeFromWatchNext?: boolean) => void

interface EditShieldProps {
  removeItem: RemoveItem
  triggerCarouselLazyload: () => void
}

const EditShield = ({ removeItem, triggerCarouselLazyload }: EditShieldProps) => (
  <div className={cn(styles.EditShield, 'tst-remove-button')}>
    <CloseButton onClick={() => {
      removeItem(true)
      triggerCarouselLazyload()
    }} />
  </div>
)

const transformPlaybackAction = (storefrontAction?: StorefrontItemPlaybackAction, progress?: ItemProgressBar) => {
  if (storefrontAction) return {
    ...storefrontAction,
    titleID: storefrontAction.titleID || storefrontAction.channelId || '',
    progress: progress && progress.percentage,
    label: storefrontAction.label
  }
  return null
}

const DisabledShield = () => (
  <div className={styles.DisabledShield}></div>
)

interface CardProps extends StorefrontStateItem {
  isEditActive?: boolean
  hoverClass?: string
  featureSwitches?: FeatureSwitches
  canLivePlayback?: boolean
  showPlayButton?: boolean
  isVerticallyVisible?: boolean
  collectionType: CollectionType
  isLazy?: boolean
  removeItem?: RemoveItem
  triggerCarouselLazyload: () => void
  pos?: number
  trailer?: TrailerFragment
  showProgressBar?: boolean
  description?: string
  isPhablet: boolean
  showPrerollsInHover?: boolean
  isHoverDisabled?: boolean
  isOnLastCarousel?: boolean
  takeFocusForCarousel?: () => void
  resetFocusForCarousel?: () => void
  onFocus?: () => void

  linearBadge?: LinearBadge //Assigned by carousel from schedule
}

interface CardState {
  hoverReady: boolean
  hoverIsExpanded: boolean,
  isRemoved: boolean

  hideThisOutput?: HideThisOutput

  enrichmentFromHover?: StorefrontItem
}

class Card extends React.PureComponent<CardProps & Args, CardState> {
  canHover: boolean
  isHover2019?: boolean
  static batchHandler: GlobalQueueHandler
  batchHandlerListener?: number

  observer?: IntersectionObserver

  constructor(props: CardProps & Args) {
    super(props)
    this.state = {
      hoverReady: false,
      hoverIsExpanded: false,
      isRemoved: false
    }
    this.canHover = !global.__NATIVEAPP__ && !this.props.isEditActive && !this.props.isDisabled &&
      safeBool(this.props.hoverInfo && this.props.hoverInfo.canHover)
    this.isHover2019 = this.props.featureSwitches && this.props.featureSwitches.hover2019 || false

    const ioOptions = {
      threshold: [0, 1],
      rootMargin: "300px"
    }

    this.observer = ((global.__SERVER__ && !global.__DEVSERVER__) || !safeFeature(this.props.featureSwitches, "hoverEager")) ? undefined :
      new IntersectionObserver(this.enrichIfIntersecting, ioOptions)

    if (!Card.batchHandler)
      Card.batchHandler = new GlobalQueueHandler(props.context, 500)
  }

  getAdditionalAttributes() {
    // To work, the hover requires
    // * the asin (or the gti) inside a data-asin attribute
    // * the lcid or channel GTI inside a data-lcid attribute
    // * the why recommended reasons, where applicable
    if (this.props.featureSwitches && this.props.featureSwitches.hover2019)
      return {}
    let attributes = {}
    if (this.props.hoverInfo && this.props.hoverInfo.canHover) {
      if (this.props.titleID) {
        attributes = { ...attributes, 'data-asin': this.props.titleID }
      }
      if (this.props.channelId) {
        attributes = { ...attributes, 'data-lcid': this.props.channelId }
      }
      if (this.props.hoverInfo && this.props.hoverInfo.whyRecommendedReasons) {
        attributes = { ...attributes, 'data-why-reasons': this.props.hoverInfo.whyRecommendedReasons }
      }
    }
    return attributes
  }

  componentDidCatch(error: Error) {
    csm.count('DVWeb::Storefront::JSFatal::Card')
    csm.logError(error, '[DVWeb::Storefront] Error while trying to render a card')
  }

  enrichIfIntersecting = (entries: IntersectionObserverEntry[]) => {
    if (!entries.some(a => a.isIntersecting) || !safeFeature(this.props.featureSwitches, "hoverEager"))
      return
    const titleID = this.props.titleID
    const enrichment = Card.batchHandler.enrichmentFor(titleID)
    if (!titleID || enrichment)
      return
    Card.batchHandler.enqueue(titleID)
    this.observer && this.observer.disconnect()
    this.observer = undefined
  }

  componentDidMount() {
    if (Card.batchHandler.enrichmentFor(this.props.titleID)) {
      this.setState({ enrichmentFromHover: Card.batchHandler.enrichmentFor(this.props.titleID) })
    } else {
      this.batchHandlerListener = Card.batchHandler.notifyOnChange(this.onBatchHandlerNotification)
    }
  }

  componentWillUnmount() {
    this.observer && this.observer.disconnect()
    typeof (this.batchHandlerListener) !== 'undefined' && Card.batchHandler.disconnectNotifier(this.batchHandlerListener)
  }

  observe = (ref: HTMLDivElement) => {
    if (this.observer && ref) {
      this.observer.observe(ref)
    }
  }

  onMouseEnterHandler = () => {
    this.canHover && this.isHover2019 && this.props.takeFocusForCarousel && this.props.takeFocusForCarousel()
    this.onHoverExpand()
  }

  setHideThisState = (out: HideThisOutput) => {
    this.setState({ hideThisOutput: out })
  }

  currentHideThisAction() {
    return this.state.hideThisOutput && this.state.hideThisOutput.hideThisAction || this.props.hideThisAction
  }

  onEnrich = (enrichment: StorefrontItem) => {
    this.setState({ enrichmentFromHover: enrichment })
  }

  onBatchHandlerNotification = (enrichmentMap: EnrichmentMap) => {
    if (enrichmentMap[this.props.titleID]) {
      this.setState({ enrichmentFromHover: enrichmentMap[this.props.titleID] })
      typeof (this.batchHandlerListener) !== 'undefined' && Card.batchHandler.disconnectNotifier(this.batchHandlerListener)
    }
  }

  onHoverExpand = () => {
    if (!this.state.hoverReady) {
      this.setState({ hoverReady: true })
    }
    this.setState({ hoverIsExpanded: true })
  }

  onHoverRetract = () => {
    this.setState({ hoverIsExpanded: false })
  }

  render() {
    const canHover = this.canHover

    const isHover2019 = this.isHover2019

    const isLive = Boolean(this.props.channelId)
    const collectionType = this.props.collectionType
    const isTwinHero = collectionType === 'TwinHero'
    const isSquare = collectionType === 'SquareCarousel'
    const isPortrait = collectionType === 'PortraitCarousel'
    const isDoubleDecker = collectionType === 'DoubleDeckerCarousel'
    const isBoxArt = collectionType === 'BoxArtCarousel'


    const wrapperClasses = cn(styles.CardWrapper, 'tst-card-wrapper', {
      [styles.isRemoved]: this.props.isRemoved,
      [styles.TwinHeroCardWrapper]: isTwinHero,
      ['tst-twin-hero-item']: isTwinHero,
      [styles.SquareCardWrapper]: isSquare,
      [styles.PortraitCardWrapper]: isPortrait,
      [styles.BoxArtCardWrapper]: isBoxArt,
      ['tst-hoverable-card']: canHover && isHover2019,
      [styles.DoubleCard]: isDoubleDecker
    })
    const isPrime = this.props.entitlements && this.props.entitlements.indexOf('prime') >= 0
    const cardClasses = cn(
      styles.TitleCard,
      'tst-title-card',
      styles.Card,
      styles.darkcard,
      {
        [styles.isDisabled]: this.props.isDisabled,
        [styles.isRemoved]: this.state.isRemoved,
        [styles.overflowOnHover]: canHover && isHover2019
      }
    )

    const hasPrimeSashFeature = Boolean(this.props.featureSwitches &&
      this.props.featureSwitches.hasPrimeSash)
    const additionalAttributes = this.getAdditionalAttributes()

    const entitlementCue = this.state.enrichmentFromHover ?
      this.state.enrichmentFromHover.cardBundle && this.state.enrichmentFromHover.cardBundle.entitlementCue :
      this.props.cardBundle && this.props.cardBundle.entitlementCue
    const showLiveBadge = Boolean(this.props.liveInfo)
    const cardContents = <Packshot
      collectionType={this.props.collectionType}
      link={this.props.link}
      image={this.props.image}
      isLazy={this.props.isLazy}
      playbackAction={this.props.playbackAction}
      hasPrimeSash={isPrime && hasPrimeSashFeature}
      isLive={isLive}
      title={this.props.title}
      alternateText={this.props.alternateText}
      canLivePlayback={this.props.canLivePlayback}
      itemType={this.props.itemType}
      isVerticallyVisible={this.props.isVerticallyVisible}
      titleID={this.props.titleID}
      isIdPlayable={this.props.properties && this.props.properties.isIdPlayable}
      featureSwitches={this.props.featureSwitches}
      showPlayButton={this.props.showPlayButton}
      playButtonClassName={cn(styles.playButton, { [styles.isLive]: isLive })}
      isRounded={showLiveBadge}
      hoverIsExpanded={canHover && this.state.hoverIsExpanded}
      entitlementCue={entitlementCue}
      station={this.props.station}
    />

    const hideThisAction = this.currentHideThisAction()
    const bentoHover = canHover &&
      isHover2019 && this.state.hoverReady

    const isChannel = this.props.channelId || (this.props.playbackAction && this.props.playbackAction.channelId)
    const maybeWrappedContents = (hideThisAction && hideThisAction.tag === "Unhide") ? <HideThisOverlay
      hideThisOutput={this.state.hideThisOutput}
      onHideThisActionChange={this.setHideThisState}
      hideThis={() =>
        hideThisAction &&
        Boolean(hideThisAction.hasTimer) &&
        this.props.removeItem && this.props.removeItem()}
      hasTimer={hideThisAction && safeBool(hideThisAction.hasTimer) || false}>
      {cardContents}
    </HideThisOverlay>
      : bentoHover ?
        <Hover
          className={styles.hover}
          placeholderClassName={styles.hoverPlaceholder}
          onMouseLeave={this.props.resetFocusForCarousel}
          offers={this.props.messages && this.props.messages.offers}
          infoboxes={this.props.messages && this.props.messages.infoboxes}
          // The Hover can infer the type, except if it's a channel
          titleIDType={isChannel ? "CHANNEL" : undefined}
          {...this.props}
          playbackAction={transformPlaybackAction(this.props.playbackAction, this.props.progress)}
          showPrerollsInHover={this.props.showPrerollsInHover}
          url={this.props.link && this.props.link.url}
          //This will keep the React component (so we don't lose Ajax-loaded state) but remove the DOM elements
          disabled={this.props.isHoverDisabled}
          direction={this.props.isOnLastCarousel ? "up" : undefined}
          removeItem={() => this.props.removeItem && this.props.removeItem()}
          hideThisButton={hideThisAction ? { onClick: this.setHideThisState, ...hideThisAction } : undefined}
          entitlementCue={entitlementCue}
          onEnrich={this.onEnrich}
        >
          {cardContents}
        </Hover> : cardContents

    const card = (
      <div className={cardClasses} ref={this.observe} onFocusCapture={this.props.onFocus}>
        <div className={styles.PackshotContainer}>
          {maybeWrappedContents}
          {this.props.showPlayButton && this.props.showProgressBar &&
            this.props.progress && <ProgressBar progress={this.props.progress} />}
          {this.props.toggleFollowingAction &&
            <FollowingButtonContainer toggleFollowingAction={this.props.toggleFollowingAction} className={styles.FollowingButtonContainer}/>
          }
        </div>
        {showLiveBadge &&
          <LiveStatus isStandalone={true} {...this.props.liveInfo} />}
        {Boolean(isSquare) &&
          (<TextOverlay link={this.props.link} title={this.props.title} />)}
        {Boolean(this.props.link) && this.props.itemType === 'Label' && !Boolean(isSquare) &&
          (<TextOverlay
            link={this.props.link}
            title={this.props.link.label}
            description={this.props.title}
            centerText={true} />)}
        {Boolean(this.props.isEditActive && !this.props.isDisabled) &&
          <EditShield removeItem={this.props.removeItem}
            triggerCarouselLazyload={this.props.triggerCarouselLazyload} />
        }
        {Boolean(this.props.isDisabled) && <DisabledShield />}
      </div>
    )

    const hoverActions = {
      'onMouseEnter': this.onMouseEnterHandler,
      'onMouseLeave': this.onHoverRetract,
      'onFocus': this.onHoverExpand,
      'onBlur': this.onHoverRetract
    }

    return (
      isDoubleDecker ?
        <div className={wrapperClasses}
          {...additionalAttributes}
          {...hoverActions}>
          {card}
        </div>
        :
        <li className={wrapperClasses}
          {...additionalAttributes}
          {...hoverActions}>
          {card}
        </li>
    )
  }

}

export default withArgs(Card) as React.ComponentType<CardProps>